import { CyclingEvent, EventCategory, EventDistance, eventCardChips, orderedVariantLabels, promotionRenderTier } from '@radevents/shared';

/**
 * Streckenchips der Card. Steht als `eventCardChips` in `shared/` — die App hatte hier eine
 * zeichengleiche Kopie. Re-Export statt Umbenennen aller Aufrufstellen.
 */
export const cardDistanceChips = eventCardChips;

/**
 * Zusatzklasse der Card-Hülle bei laufender bezahlter Hervorhebung (siehe App.css, Abschnitt
 * „Bezahlte Hervorhebung der Card"). Leer, wenn nichts gebucht ist oder die Buchung nicht läuft.
 *
 * Steht hier und nicht in den Card-Komponenten, weil das `<article>` in den Seiten liegt: Hero und
 * Body sind nur die Inhalte darin.
 *
 * [asStage] sagt, ob die Seite das Bühnen-Layout (Stufe 1b) auch wirklich zeichnet. Nur die
 * Übersicht tut das, und dort höchstens einmal je Ergebnisseite — überall sonst bekommt eine
 * gebuchte Bühne die Hülle des Signatur-Rahmens, weil ihr Inhalt dort als normale Card gerendert
 * wird und die Bühnen-Klasse dessen Layout zerlegen würde.
 */
export const promoCardClass = (event: CyclingEvent, asStage = false): string => {
  const tier = promotionRenderTier(event);
  if (!tier) return '';
  if (tier === 'STAGE') return asStage ? 'event-card--promo-stage' : 'event-card--promo-frame';
  return tier === 'FRAME' ? 'event-card--promo-frame' : 'event-card--promo-inverted';
};

/**
 * Ist für dieses Event die Bühne gebucht *und* auslieferbar? Ohne Event-Foto fällt sie auf den
 * Signatur-Rahmen zurück (siehe `promotionRenderTier`), dann ist das hier false.
 */
export const isStageCard = (event: CyclingEvent): boolean => promotionRenderTier(event) === 'STAGE';

/**
 * Hero-Bild einer Event-Card (Design-Handout "Event-Card Hero").
 *
 * Fallback-Kette: Event-Foto → Veranstalter-Header-Foto → generiertes Motiv aus den Streckendaten.
 * Es gibt bewusst keinen leeren Platzhalter: die Übersicht sieht auch dann "bebildert" aus, wenn
 * für die allermeisten Events kein Foto hinterlegt ist.
 *
 * `imageUrl` und `organizerPhotoUrl` liefert das Backend fertig aufgelöst am Event mit
 * (ein Upload gewinnt dort bereits gegen eine externe Bild-URL).
 */

/** Himmel-oben, Himmel-Horizont, Bergrücken-mitte, Bergrücken-vorne. */
const HERO_TINT: Record<EventCategory, [string, string, string, string]> = {
  [EventCategory.ROAD]: ['#FBEFF3', '#E7C3D1', '#C36A8A', '#8A2F4F'],
  [EventCategory.GRAVEL]: ['#FAF2E7', '#EAD0A6', '#C0904F', '#835626'],
  [EventCategory.MTB]: ['#ECF5EE', '#C7E3CF', '#4FA372', '#1E6E45'],
};

/** Deterministischer PRNG aus einem String-Seed — gleicher Seed, gleiches Motiv. */
const rng = (seed: string): (() => number) => {
  let s = (Array.from(seed).reduce((a, c) => a + c.charCodeAt(0), 0) * 2654435761) % 2147483647 || 1;
  return () => (s = (s * 48271) % 2147483647) / 2147483647;
};

/** Eine geglättete Bergrücken-Silhouette als SVG-Path. */
const ridgePath = (rand: () => number, W: number, H: number, base: number, amp: number, tilt: number): string => {
  const n = 8;
  const ys: number[] = [];
  for (let i = 0; i <= n; i++) ys.push(base - amp * (0.35 + 0.65 * rand()) - tilt * (i / n));
  let d = `M0 ${H} L0 ${ys[0].toFixed(1)}`;
  for (let i = 1; i <= n; i++) {
    const px = ((i - 1) / n) * W;
    const cx = (i / n) * W;
    const mx = (px + cx) / 2;
    d += ` Q${px.toFixed(1)} ${ys[i - 1].toFixed(1)} ${mx.toFixed(1)} ${((ys[i - 1] + ys[i]) / 2).toFixed(1)}`;
  }
  return `${d} L${W} ${ys[n].toFixed(1)} L${W} ${H} Z`;
};

/** Höchste Höhenmeter-Angabe des Events — formt die Silhouette (bergig = markanter). */
const maxElevation = (event: CyclingEvent): number =>
  event.distances.reduce((max, d) => Math.max(max, d.elevationMeters ?? 0), 0);

/**
 * Generiert das Default-Motiv als data-URI. Seed ist die Event-ID (nicht der Titel), damit das
 * Motiv bei einer Umbenennung des Events gleich bleibt.
 */
export const buildHeroSvg = (event: CyclingEvent): string => {
  const t = HERO_TINT[event.category] ?? HERO_TINT[EventCategory.ROAD];
  const W = 400;
  const H = 150;
  const seed = String(event.id);
  const climby = maxElevation(event) > 1600;
  const back = ridgePath(rng(`${seed}b`), W, H, H * 0.6, climby ? 22 : 14, 6);
  const mid = ridgePath(rng(`${seed}m`), W, H, H * 0.78, climby ? 40 : 26, 10);
  const front = ridgePath(rng(`${seed}f`), W, H, H * 0.96, climby ? 34 : 24, -4);
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${W} ${H}' preserveAspectRatio='xMidYMid slice'>` +
    `<defs><linearGradient id='s' x1='0' y1='0' x2='0' y2='1'>` +
    `<stop offset='0' stop-color='${t[0]}'/><stop offset='1' stop-color='${t[1]}'/></linearGradient></defs>` +
    `<rect width='${W}' height='${H}' fill='url(#s)'/>` +
    `<path d='${back}' fill='${t[2]}' opacity='0.32'/>` +
    `<path d='${mid}' fill='${t[2]}' opacity='0.62'/>` +
    `<path d='${front}' fill='${t[3]}'/>` +
    `</svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
};

/** Echtes Foto (Event oder Veranstalter) — null, wenn nur das generierte Motiv greift. */
export const heroPhotoUrl = (event: CyclingEvent): string | null =>
  event.imageUrl || event.organizerPhotoUrl || null;

/** Fertiger CSS-`background-image`-Wert für den Hero-Container. */
export const eventHeroBg = (event: CyclingEvent): string => {
  const photo = heroPhotoUrl(event);
  return photo ? `url("${photo}")` : buildHeroSvg(event);
};

/**
 * Distanz-Zeile der Card: bis zu 3 Distanzen als `42 / 88 / 140 km` (nur EIN „km" am Ende,
 * Ziffern tabellarisch), der Rest als dezentes „+N weitere". Bei mehr als 3 zeigen wir die
 * kleinste, mittlere und größte — repräsentativer als einfach die ersten drei.
 */
export const formatCardDistances = (distances: EventDistance[]): { main: string; more: string | null } => {
  const labels = orderedVariantLabels(distances);
  if (labels.length === 0) return { main: '', more: null };
  if (labels.length <= 3) return { main: labels.join(' / '), more: null };
  const shown = [labels[0], labels[Math.floor(labels.length / 2)], labels[labels.length - 1]];
  return { main: shown.join(' / '), more: `+${labels.length - 3} weitere` };
};

/**
 * Distanzen als einzelne Chips (Design "bildlose Card"): aufsteigend sortiert, maximal 3 Chips,
 * der Rest als dezenter „+N weitere"-Chip. Anders als `formatCardDistances` (eine Text-Zeile)
 * liefert das die einzelnen Beschriftungen für die Chip-Reihe. Bei mehr als 3 Distanzen werden
 * kleinste, mittlere und längste gezeigt (statt der ersten drei), damit die Spannbreite sichtbar bleibt.
 */

/** Markenfarben für den Monogramm-Fallback des Veranstalter-Badges. */
const ORG_PALETTE = ['#922C57', '#D23A2B', '#1F8A5B', '#2E63C4', '#9A5B2E', '#A82F5A'];

export const orgColor = (name: string): string =>
  ORG_PALETTE[Array.from(name || '').reduce((a, c) => a + c.charCodeAt(0), 0) % ORG_PALETTE.length];

/** 2-Buchstaben-Monogramm aus dem Vereinsnamen (Rechtsform/Präfixe wie RSV, e.V. bereinigt). */
export const orgMonogram = (name: string): string => {
  const words = (name || '?')
    .replace(/e\.?\s*V\.?/gi, '')
    .replace(/\b(RSV|RV|RC|SV|TSV|MTB|Team)\b/gi, ' $& ')
    .replace(/[^A-Za-zÄÖÜäöü0-9 ]/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return (name || '?').replace(/[^A-Za-zÄÖÜäöü0-9]/g, '').slice(0, 2).toUpperCase();
};

/**
 * Fokuspunkt als CSS-Wert fuer `background-position` / `object-position`.
 *
 * Card und Detailseite schneiden dasselbe Bild in unterschiedlichen Seitenverhaeltnissen zu.
 * Statt das Bild fest zuzuschneiden (was in einem der beiden immer daneben laege) verschieben
 * wir den Ausschnitt so, dass der gewaehlte Punkt sichtbar bleibt.
 */
export const heroFocus = (event: CyclingEvent): string =>
  `${event.imageFocusX ?? 50}% ${event.imageFocusY ?? 50}%`;
